import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';

import { useCreateCustomCondition } from '~/api/conditions';
import { Button, Dialog, Text } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { clsx } from '~/styles/utils';
import { includes } from '~/utils/arrays';
import { toast } from '~/utils/toast';

import type {
  Answers,
  CreateCustomConditionProps as Props,
  TCustomConditionStep,
  TModifyHow,
  TReproduceHow,
  TReproduceHowMany,
  TShareHow,
  TUseHow,
  TUseHowLong,
  TUseWhere,
} from './types';

import { Fieldset } from '../components/Fieldset';
import { InputFieldCheckbox } from '../components/InputFieldCheckbox';
import { InputFieldRadio } from '../components/InputFieldRadio';
import { YesNoInput } from '../components/YesNoInput';
import {
  CustomConditionStep,
  initialState,
  ModifyHow,
  ReproduceHow,
  ReproduceHowMany,
  ShareHow,
  UseHow,
  UseHowLong,
  UseWhere,
} from './consts';
import {
  gridStyle,
  otherInputStyle,
  overviewFormStyle,
  overviewItemTitleStyle,
  previousStyle,
  wideDialogStyle,
  wizardContentStyle,
} from './CreateCustomConditionModal.css';

export const CreateCustomConditionModal = ({ onClose }: Props) => {
  const mutation = useCreateCustomCondition();

  const { t } = useTranslation(['common', 'settings']);

  const [currentStep, setCurrentStep] = useState<TCustomConditionStep | null>(CustomConditionStep.START);
  const [answers, setAnswers] = useState<Answers>(initialState);
  const [customConditionsName, setCustomConditionsName] = useState('');
  const [stepsHistory, setStepsHistory] = useState<TCustomConditionStep[]>([]);

  const saveStepAndGoToNext = <K extends TCustomConditionStep>(key: K, value: Answers[K]) => {
    const newAnswers = {
      ...answers,
      [key]: value,
    };
    setAnswers(newAnswers);

    if (currentStep) {
      const newStepsHistory = [...stepsHistory];
      newStepsHistory.push(currentStep);
      setStepsHistory(newStepsHistory);
    }

    if (currentStep === CustomConditionStep.OVERVIEW) {
      return mutation.mutate(
        {
          name: customConditionsName,
          wizardData: answers,
        },
        {
          onSuccess: () => setCurrentStep(CustomConditionStep.THANKS),
          onError: () => toast.danger({ description: t('common:error') }),
        },
      );
    }

    const nextStep = getNextStep(value);
    invariant(nextStep !== undefined, `getNextStep() returned undefined for step "${currentStep}"`);

    if (nextStep !== null) {
      setCurrentStep(nextStep as TCustomConditionStep);
    } else {
      onClose();
      setCurrentStep(CustomConditionStep.START);
      setAnswers(initialState);
    }
  };

  const getNextStep = (answer: Answers[keyof Answers]) => {
    switch (currentStep) {
      case CustomConditionStep.START:
        return CustomConditionStep.ALREADY_AGREEMENT;

      case CustomConditionStep.ALREADY_AGREEMENT:
        return answer === true ? CustomConditionStep.ALREADY_AGREEMENT_PAPER : CustomConditionStep.ALREADY_AGREEMENT_NO;

      case CustomConditionStep.ALREADY_AGREEMENT_NO:
        return CustomConditionStep.TRANSFER_COPYRIGHT;

      case CustomConditionStep.ALREADY_AGREEMENT_PAPER:
        return answer === true ? CustomConditionStep.ALREADY_AGREEMENT_YES_PAPER : CustomConditionStep.ALREADY_AGREEMENT_NO_PAPER;

      case CustomConditionStep.ALREADY_AGREEMENT_NO_PAPER:
        return CustomConditionStep.TRANSFER_COPYRIGHT;

      case CustomConditionStep.ALREADY_AGREEMENT_YES_PAPER:
        return null;

      case CustomConditionStep.TRANSFER_COPYRIGHT:
        return answer === true ? CustomConditionStep.TRANSFER_COPYRIGHT_YES : CustomConditionStep.TRANSFER_COPYRIGHT_NO;

      case CustomConditionStep.TRANSFER_COPYRIGHT_YES:
        return null;

      case CustomConditionStep.TRANSFER_COPYRIGHT_NO:
        return CustomConditionStep.USE_WHERE;

      case CustomConditionStep.USE_WHERE:
        return CustomConditionStep.USE_HOW_LONG;

      case CustomConditionStep.USE_HOW_LONG:
        return CustomConditionStep.USE_HOW;

      case CustomConditionStep.USE_HOW: {
        if (answers[CustomConditionStep.USE_HOW][0] === true) return CustomConditionStep.REPRODUCE_HOW;
        if (answers[CustomConditionStep.USE_HOW][1] === true) return CustomConditionStep.SHARE_HOW;
        if (answers[CustomConditionStep.USE_HOW][2] === true) return CustomConditionStep.MODIFY_HOW;
        return CustomConditionStep.OVERVIEW;
      }
      case CustomConditionStep.REPRODUCE_HOW: {
        if ((answer as Answers['reproduce_how'])[0] || (answer as Answers['reproduce_how'])[1] || (answer as Answers['reproduce_how'])[2])
          return CustomConditionStep.REPRODUCE_HOW_MANY;
        if (answers[CustomConditionStep.USE_HOW][1] === true) return CustomConditionStep.SHARE_HOW;
        if (answers[CustomConditionStep.USE_HOW][2] === true) return CustomConditionStep.MODIFY_HOW;
        return CustomConditionStep.OVERVIEW;
      }
      case CustomConditionStep.REPRODUCE_HOW_MANY: {
        if (answers[CustomConditionStep.USE_HOW][1] === true) return CustomConditionStep.SHARE_HOW;
        if (answers[CustomConditionStep.USE_HOW][2] === true) return CustomConditionStep.MODIFY_HOW;
        return CustomConditionStep.OVERVIEW;
      }
      case CustomConditionStep.SHARE_HOW: {
        if (answers[CustomConditionStep.USE_HOW][2] === true) return CustomConditionStep.MODIFY_HOW;
        return CustomConditionStep.OVERVIEW;
      }
      case CustomConditionStep.MODIFY_HOW:
        return CustomConditionStep.OVERVIEW;

      case CustomConditionStep.OVERVIEW:
        return CustomConditionStep.THANKS;

      case CustomConditionStep.THANKS:
        return null;
    }
  };

  const selectOption = <K extends ChoiceWithTextStep>(step: K, option: NonNullable<Answers[K]['selected']>) => {
    setAnswers({ ...answers, [step]: { selected: option, text: needsText(step, option) ? answers[step].text : null } });
  };

  const setText = (step: ChoiceWithTextStep, text: string) => {
    setAnswers({ ...answers, [step]: { ...answers[step], text } });
  };

  const canGoToNextStep = (() => {
    switch (currentStep) {
      case CustomConditionStep.ALREADY_AGREEMENT:
      case CustomConditionStep.ALREADY_AGREEMENT_PAPER:
      case CustomConditionStep.TRANSFER_COPYRIGHT:
      case CustomConditionStep.MODIFY_HOW:
        return answers[currentStep] !== null;

      case CustomConditionStep.USE_WHERE:
      case CustomConditionStep.USE_HOW_LONG:
      case CustomConditionStep.REPRODUCE_HOW_MANY:
      case CustomConditionStep.SHARE_HOW: {
        const { selected, text } = answers[currentStep];
        return selected !== null && (!needsText(currentStep, selected) || hasText(text));
      }

      case CustomConditionStep.USE_HOW:
        return Object.values(answers[currentStep]).some((answer) => answer === true);

      case CustomConditionStep.REPRODUCE_HOW: {
        const answer = answers[currentStep];
        return Object.values(answer).some((value) => value === true) && (!answer[ReproduceHow.SPECIFIC] || hasText(answer.text));
      }

      default:
        return false;
    }
  })();

  const goToNextStep = () => {
    if (currentStep && canGoToNextStep) saveStepAndGoToNext(currentStep, answers[currentStep]);
  };

  const nextButton = (
    <Button disabled={!canGoToNextStep} type="submit">
      {t('settings:general.customConditions.wizard.common_answers.next')}
    </Button>
  );

  const renderTextAndNext = (step: ChoiceWithTextStep) => (
    <>
      {needsText(step, answers[step].selected) && (
        <OtherInput
          onValueChange={(text) => setText(step, text)}
          placeholder={t(`settings:general.customConditions.wizard.${step}.other_placeholder`)}
          value={answers[step].text ?? ''}
        />
      )}
      {nextButton}
    </>
  );

  const goToPreviousStep = () => {
    const newStepsHistory = [...stepsHistory];
    const previousStep = newStepsHistory.pop();

    setStepsHistory(newStepsHistory);
    setCurrentStep(previousStep || null);
  };

  const title =
    currentStep !== CustomConditionStep.OVERVIEW && currentStep !== CustomConditionStep.THANKS
      ? t(`settings:general.customConditions.wizard.${CustomConditionStep.START}.title`)
      : currentStep === CustomConditionStep.OVERVIEW
        ? t(`settings:general.customConditions.wizard.${CustomConditionStep.OVERVIEW}.question`)
        : t(`settings:general.customConditions.wizard.${CustomConditionStep.THANKS}.question`);

  return (
    <Dialog.Root onOpenChange={(open) => !open && onClose()} open>
      <Dialog.Body className={currentStep === CustomConditionStep.OVERVIEW ? wideDialogStyle : undefined}>
        <Dialog.Header title={title} />

        {currentStep === CustomConditionStep.START && (
          <>
            <p>{t(`settings:general.customConditions.wizard.${CustomConditionStep.START}.description`)}</p>

            <div className={wizardContentStyle}>
              <Button onClick={() => saveStepAndGoToNext(currentStep, true)}>
                {t(`settings:general.customConditions.wizard.${currentStep}.ok`)}
              </Button>
            </div>
          </>
        )}

        {!includes([CustomConditionStep.START, CustomConditionStep.OVERVIEW, CustomConditionStep.THANKS] as const, currentStep) && (
          <div>
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                goToNextStep();
              }}
            >
              {includes(
                [
                  CustomConditionStep.ALREADY_AGREEMENT,
                  CustomConditionStep.ALREADY_AGREEMENT_PAPER,
                  CustomConditionStep.TRANSFER_COPYRIGHT,
                ] as const,
                currentStep,
              ) && (
                <Fieldset
                  footer={nextButton}
                  key={currentStep}
                  onValueChange={(value: 'no' | 'yes') => setAnswers({ ...answers, [currentStep]: value === 'yes' })}
                  step={currentStep}
                  value={answers[currentStep] === null ? undefined : answers[currentStep] ? 'yes' : 'no'}
                >
                  <YesNoInput />
                </Fieldset>
              )}
              {(currentStep === CustomConditionStep.ALREADY_AGREEMENT_NO ||
                currentStep === CustomConditionStep.ALREADY_AGREEMENT_NO_PAPER) && (
                <Fieldset step={currentStep}>
                  <Button onClick={() => saveStepAndGoToNext(currentStep, true)}>
                    {t('settings:general.customConditions.wizard.common_answers.start_questions')}
                  </Button>
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.ALREADY_AGREEMENT_YES_PAPER && (
                <Fieldset step={currentStep}>
                  <Button emphasis="secondary" onClick={() => saveStepAndGoToNext(currentStep, true)}>
                    {t('settings:general.customConditions.wizard.common_answers.close')}
                  </Button>
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.TRANSFER_COPYRIGHT_YES && (
                <Fieldset step={currentStep}>
                  <Button icon="CloseSmall" onClick={() => saveStepAndGoToNext(currentStep, true)}>
                    {t('settings:general.customConditions.wizard.common_answers.close')}
                  </Button>
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.TRANSFER_COPYRIGHT_NO && (
                <Fieldset step={currentStep}>
                  <Button onClick={() => saveStepAndGoToNext(currentStep, true)}>
                    {t('settings:general.customConditions.wizard.common_answers.ok')}
                  </Button>
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.USE_WHERE && (
                <Fieldset
                  footer={renderTextAndNext(currentStep)}
                  key={currentStep}
                  onValueChange={(option: TUseWhere) => selectOption(currentStep, option)}
                  step={currentStep}
                  value={answers[currentStep].selected}
                >
                  {[UseWhere.BELGIUM, UseWhere.EUROPE, UseWhere.WORLD, UseWhere.OTHER].map((option) => (
                    <InputFieldRadio key={option} value={option}>
                      {t(`settings:general.customConditions.wizard.use_where.options.${option}`)}
                    </InputFieldRadio>
                  ))}
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.USE_HOW_LONG && (
                <Fieldset
                  footer={renderTextAndNext(currentStep)}
                  key={currentStep}
                  onValueChange={(option: TUseHowLong) => selectOption(currentStep, option)}
                  step={currentStep}
                  value={answers[currentStep].selected}
                >
                  {[UseHowLong.FULL, UseHowLong.OTHER].map((option) => (
                    <InputFieldRadio key={option} value={option}>
                      {t(`settings:general.customConditions.wizard.use_how_long.options.${option}`)}
                    </InputFieldRadio>
                  ))}
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.USE_HOW && (
                <Fieldset footer={nextButton} step={currentStep}>
                  {[UseHow.REPRODUCE, UseHow.SHARE_PUBLIC, UseHow.EDIT].map((option) => {
                    return (
                      <InputFieldCheckbox
                        checked={answers[currentStep][option]}
                        key={option}
                        onCheckedChange={(checked) =>
                          setAnswers({ ...answers, [currentStep]: { ...answers[currentStep], [option]: checked } })
                        }
                        step={currentStep}
                      >
                        {t(`settings:general.customConditions.wizard.use_how.options.${option}`)}
                      </InputFieldCheckbox>
                    );
                  })}
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.REPRODUCE_HOW && (
                <Fieldset footer={nextButton} step={currentStep}>
                  {[ReproduceHow.ANY, ReproduceHow.SPECIFIC, ReproduceHow.ELECTRONIC_FIXED, ReproduceHow.ELECTRONIC_ONLINE].map(
                    (option) => {
                      return (
                        <Fragment key={option}>
                          <InputFieldCheckbox
                            checked={answers[currentStep][option]}
                            onCheckedChange={() => {
                              if (option === ReproduceHow.ANY) {
                                setAnswers({
                                  ...answers,
                                  [currentStep]: {
                                    0: !answers[currentStep][option],
                                    1: false,
                                    2: false,
                                    3: false,
                                    text: null,
                                  },
                                });
                              } else {
                                setAnswers({
                                  ...answers,
                                  [currentStep]: {
                                    ...answers[currentStep],
                                    [option]: !answers[currentStep][option],
                                    0: false,
                                    text:
                                      option === ReproduceHow.SPECIFIC && answers[currentStep][option] ? null : answers[currentStep].text,
                                  },
                                });
                              }
                            }}
                            step={currentStep}
                          >
                            {t(`settings:general.customConditions.wizard.reproduce_how.options.${option}`)}
                          </InputFieldCheckbox>
                          {option === ReproduceHow.SPECIFIC && answers[currentStep][option] && (
                            <OtherInput
                              className={otherInputStyle}
                              onValueChange={(text) => setAnswers({ ...answers, [currentStep]: { ...answers[currentStep], text } })}
                              placeholder={t('settings:general.customConditions.wizard.reproduce_how.other_placeholder')}
                              value={answers[currentStep].text ?? ''}
                            />
                          )}
                        </Fragment>
                      );
                    },
                  )}
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.REPRODUCE_HOW_MANY && (
                <Fieldset
                  footer={renderTextAndNext(currentStep)}
                  key={currentStep}
                  onValueChange={(option: TReproduceHowMany) => selectOption(currentStep, option)}
                  step={currentStep}
                  value={answers[currentStep].selected}
                >
                  {[ReproduceHowMany.UNLIMITED, ReproduceHowMany.LIMITED].map((option) => (
                    <InputFieldRadio key={option} value={option}>
                      {t(`settings:general.customConditions.wizard.reproduce_how_many.options.${option}`)}
                    </InputFieldRadio>
                  ))}
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.SHARE_HOW && (
                <Fieldset
                  footer={renderTextAndNext(currentStep)}
                  key={currentStep}
                  onValueChange={(option: TShareHow) => selectOption(currentStep, option)}
                  step={currentStep}
                  value={answers[currentStep].selected}
                >
                  {[ShareHow.PUBLISHED, ShareHow.DISPOSABLE, ShareHow.BOOK, ShareHow.PROMO].map((option) => (
                    <InputFieldRadio key={option} value={option}>
                      {t(`settings:general.customConditions.wizard.share_how.options.${option}`)}
                    </InputFieldRadio>
                  ))}
                </Fieldset>
              )}
              {currentStep === CustomConditionStep.MODIFY_HOW && (
                <Fieldset
                  footer={nextButton}
                  key={currentStep}
                  onValueChange={(option: TModifyHow) => setAnswers({ ...answers, [currentStep]: option })}
                  step={currentStep}
                  value={answers[currentStep]}
                >
                  {[ModifyHow.EDIT, ModifyHow.TRANSLATE].map((option) => (
                    <InputFieldRadio key={option} value={option}>
                      {t(`settings:general.customConditions.wizard.modify_how.options.${option}`)}
                    </InputFieldRadio>
                  ))}
                </Fieldset>
              )}
            </Form>

            <Button className={previousStyle} emphasis="tertiary" icon="ArrowLeft" onClick={() => goToPreviousStep()}>
              {t('settings:general.customConditions.wizard.common_answers.previous')}
            </Button>
          </div>
        )}

        {currentStep === CustomConditionStep.OVERVIEW && (
          <div>
            <div className={clsx(wizardContentStyle, gridStyle)}>
              <div>
                <Text className={overviewItemTitleStyle}>{t('settings:general.customConditions.wizard.use_where.question')}</Text>
                <Text>
                  {t(
                    `settings:general.customConditions.wizard.use_where.options.${answers[CustomConditionStep.USE_WHERE].selected as TUseWhere}`,
                  )}
                  {answers[CustomConditionStep.USE_WHERE].selected === 3 && ` (${answers[CustomConditionStep.USE_WHERE].text})`}
                </Text>
              </div>
              <div>
                <Text className={overviewItemTitleStyle}>{t('settings:general.customConditions.wizard.use_how_long.question')}</Text>
                <Text>
                  {t(
                    `settings:general.customConditions.wizard.use_how_long.options.${
                      answers[CustomConditionStep.USE_HOW_LONG].selected as TUseHowLong
                    }`,
                  )}
                  {answers[CustomConditionStep.USE_HOW_LONG].selected === 1 && ` (${answers[CustomConditionStep.USE_HOW_LONG].text})`}
                </Text>
              </div>
              <div>
                <Text className={overviewItemTitleStyle}>{t('settings:general.customConditions.wizard.use_how.question')}</Text>
                <Text>
                  {Object.values(answers[CustomConditionStep.USE_HOW]).map((answer, index) => {
                    if (answer === true) {
                      return (
                        <span key={index}>
                          {t(`settings:general.customConditions.wizard.use_how.options.${index as TUseHow}`)} <br />
                        </span>
                      );
                    }
                  })}
                </Text>
              </div>
              {answers[CustomConditionStep.USE_HOW][0] === true && (
                <div>
                  <Text className={overviewItemTitleStyle}>{t('settings:general.customConditions.wizard.reproduce_how.question')}</Text>
                  <Text>
                    {Object.values(answers[CustomConditionStep.REPRODUCE_HOW]).map((answer, index) => {
                      if (index !== 4 && answer === true) {
                        return (
                          <span key={index}>
                            {t(`settings:general.customConditions.wizard.reproduce_how.options.${index as TReproduceHow}`)}
                            {index === 1 && answer && ` (${answers[CustomConditionStep.REPRODUCE_HOW].text})`}
                            <br />
                          </span>
                        );
                      }
                    })}
                  </Text>
                </div>
              )}
              {answers[CustomConditionStep.USE_HOW][0] === true &&
                (answers[CustomConditionStep.REPRODUCE_HOW][0] === true ||
                  answers[CustomConditionStep.REPRODUCE_HOW][1] === true ||
                  answers[CustomConditionStep.REPRODUCE_HOW][2] === true) && (
                  <div>
                    <Text className={overviewItemTitleStyle}>
                      {t('settings:general.customConditions.wizard.reproduce_how_many.question')}
                    </Text>
                    <Text>
                      {t(
                        `settings:general.customConditions.wizard.reproduce_how_many.options.${
                          answers[CustomConditionStep.REPRODUCE_HOW_MANY].selected as TReproduceHowMany
                        }`,
                      )}
                      {answers[CustomConditionStep.REPRODUCE_HOW_MANY].selected === 1 &&
                        ` (${answers[CustomConditionStep.REPRODUCE_HOW_MANY].text})`}
                    </Text>
                  </div>
                )}
              {answers[CustomConditionStep.USE_HOW][1] === true && (
                <div>
                  <Text className={overviewItemTitleStyle}>{t('settings:general.customConditions.wizard.share_how.question')}</Text>
                  <Text>
                    {answers[CustomConditionStep.SHARE_HOW].selected === 0 || answers[CustomConditionStep.SHARE_HOW].selected === 1
                      ? t(
                          `settings:general.customConditions.wizard.share_how.options.${answers[CustomConditionStep.SHARE_HOW].selected as TShareHow}`,
                        ).replace('...', answers[CustomConditionStep.SHARE_HOW].text ?? '')
                      : t(
                          `settings:general.customConditions.wizard.share_how.options.${
                            answers[CustomConditionStep.SHARE_HOW].selected as TShareHow
                          }`,
                        )}
                  </Text>
                </div>
              )}
              {answers[CustomConditionStep.USE_HOW][2] === true && (
                <div>
                  <Text className={overviewItemTitleStyle}>{t('settings:general.customConditions.wizard.modify_how.question')}</Text>
                  <Text>
                    {t(
                      `settings:general.customConditions.wizard.modify_how.options.${answers[CustomConditionStep.MODIFY_HOW] as TModifyHow}`,
                    )}
                  </Text>
                </div>
              )}
              <Form
                className={overviewFormStyle}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (hasText(customConditionsName)) saveStepAndGoToNext(currentStep, true);
                }}
              >
                <Field.Root name="customConditionsName">
                  <Field.Label>{t('settings:general.customConditions.wizard.overview.name.label')}</Field.Label>
                  <Input
                    id="customConditionsName"
                    onValueChange={setCustomConditionsName}
                    placeholder={t('settings:general.customConditions.wizard.overview.name.placeholder')}
                    required
                    value={customConditionsName}
                  />
                </Field.Root>

                <Button disabled={!hasText(customConditionsName)} icon="Save" loading={mutation.isPending} type="submit">
                  {t('settings:general.customConditions.wizard.overview.finish')}
                </Button>
              </Form>
            </div>

            <Button className={previousStyle} emphasis="tertiary" icon="ArrowLeft" onClick={() => goToPreviousStep()}>
              {t('settings:general.customConditions.wizard.common_answers.previous')}
            </Button>
          </div>
        )}

        {currentStep === CustomConditionStep.THANKS && (
          <div className={wizardContentStyle}>
            <p>
              <span className={overviewItemTitleStyle}>{t('settings:general.customConditions.wizard.thanks.subtitle')}</span>
              <br />
              {t('settings:general.customConditions.wizard.thanks.explanation')}
            </p>

            <Dialog.Actions>
              <Button onClick={() => saveStepAndGoToNext(currentStep, true)}>
                {t('settings:general.customConditions.wizard.thanks.close')}
              </Button>
            </Dialog.Actions>
          </div>
        )}
      </Dialog.Body>
    </Dialog.Root>
  );
};

type ChoiceWithTextStep =
  | typeof CustomConditionStep.REPRODUCE_HOW_MANY
  | typeof CustomConditionStep.SHARE_HOW
  | typeof CustomConditionStep.USE_HOW_LONG
  | typeof CustomConditionStep.USE_WHERE;

/** The options that reveal a free-text field, which must then be filled in. */
const textOptions: Record<ChoiceWithTextStep, readonly number[]> = {
  [CustomConditionStep.REPRODUCE_HOW_MANY]: [ReproduceHowMany.LIMITED],
  [CustomConditionStep.SHARE_HOW]: [ShareHow.PUBLISHED, ShareHow.DISPOSABLE],
  [CustomConditionStep.USE_HOW_LONG]: [UseHowLong.OTHER],
  [CustomConditionStep.USE_WHERE]: [UseWhere.OTHER],
};

const needsText = (step: ChoiceWithTextStep, option: number | null) => option !== null && textOptions[step].includes(option);

const hasText = (text: string | null) => !!text?.trim();

type OtherInputProps = {
  className?: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  value: string;
};

/** The free-text answer that some options reveal. Its label repeats the placeholder, so it stays visually hidden. */
const OtherInput = ({ className, onValueChange, placeholder, value }: OtherInputProps) => (
  <Field.Root className={className} name="other">
    <Field.Label srOnly>{placeholder}</Field.Label>

    <Input onValueChange={onValueChange} placeholder={placeholder} value={value} />
  </Field.Root>
);
